import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  nuloSeNaoEncontrado,
  type PaginaComPermissoes,
} from '@/modulos/operacao/cadastros/leitura';
import { parametrosDaListagem } from '@/modulos/operacao/comum/contrato';
import { naoInsistirEmRecusa } from '@/modulos/operacao/comum/dados';
import { chamarApi, type ErroApi } from '@/nucleo/auth/cliente-api';
import { exportarCsv } from '@/nucleo/exportarCsv';
import { mascaraPlaca } from '@/nucleo/formato/documentos';

import type { FiltroDeVeiculos, TipoDeVeiculo, Veiculo, VeiculoDetalhe } from './contrato';

/**
 * A camada de dados da frota: lista, detalhe, tipos e exportação, todos lidos da
 * API (`apps/api/src/modulos/veiculos`). Só leitura — cadastro e mudança de
 * veículo são da J-05 (S01, S05).
 */

export const CAMINHO_DA_API = '/bff/v1/veiculos';

export const CHAVES_DE_VEICULOS = {
  lista: (filtro: FiltroDeVeiculos) => ['veiculos', 'lista', filtro] as const,
  exportacao: (filtro: Omit<FiltroDeVeiculos, 'pagina' | 'tamanho'>) =>
    ['veiculos', 'exportacao', filtro] as const,
  tipos: ['veiculos', 'tipos'] as const,
  detalhe: (slug: string) => ['veiculos', 'detalhe', slug] as const,
};

/** Teto da exportacao: o mesmo da API (`TETO_DA_EXPORTACAO`). */
export const TETO_DA_EXPORTACAO = 1000;

export function useVeiculos(
  filtro: FiltroDeVeiculos,
): UseQueryResult<PaginaComPermissoes<Veiculo>, ErroApi> {
  return useQuery<PaginaComPermissoes<Veiculo>, ErroApi>({
    queryKey: CHAVES_DE_VEICULOS.lista(filtro),
    queryFn: ({ signal }) =>
      chamarApi<PaginaComPermissoes<Veiculo>>(
        `${CAMINHO_DA_API}${parametrosDaListagem({ ...filtro })}`,
        { sinal: signal },
      ),
    placeholderData: (anterior) => anterior,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * O veiculo de `/operacao/veiculos/:slug`. NUNCA LANÇA por 404: o veiculo que
 * nao existe, ou que o recorte nao mostra, vira `null` e a tela diz "nao
 * encontrado" — nao e falha de sistema.
 */
export function useVeiculo(
  slug: string | undefined,
): UseQueryResult<VeiculoDetalhe | null, ErroApi> {
  return useQuery<VeiculoDetalhe | null, ErroApi>({
    queryKey: CHAVES_DE_VEICULOS.detalhe(slug ?? ''),
    enabled: !!slug,
    queryFn: ({ signal }) =>
      chamarApi<VeiculoDetalhe>(`${CAMINHO_DA_API}/${encodeURIComponent(slug ?? '')}`, {
        sinal: signal,
      }).catch(nuloSeNaoEncontrado),
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Os tipos de veiculo (`public.tipos_veiculo`), para o filtro e o selo da coluna Tipo.
 * Cache longo: o catalogo muda pouco.
 */
export function useTiposDeVeiculo(): UseQueryResult<TipoDeVeiculo[], ErroApi> {
  return useQuery<TipoDeVeiculo[], ErroApi>({
    queryKey: CHAVES_DE_VEICULOS.tipos,
    queryFn: ({ signal }) =>
      chamarApi<TipoDeVeiculo[]>(`${CAMINHO_DA_API}/tipos`, { sinal: signal }),
    staleTime: 10 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}

/**
 * Leitura sincrona por slug, que a tela de edicao (`CadastroEdicaoPage`) ainda
 * usa. Nao ha mais copia local da frota: o detalhe vem da API, por `useVeiculo`,
 * e a edicao de cadastro fica fora desta rodada (so a J-05 muda veiculo). Sem
 * registro, a tela de edicao mostra o estado de erro dela.
 */
export function obterVeiculoPorSlug(_slug: string | undefined): Veiculo | undefined {
  return undefined;
}

/**
 * Placa ja cadastrada, para o formulario de novo veiculo. Nao ha rota de
 * conferencia ao vivo, e o formulario nao grava (mostra o aviso de
 * indisponibilidade); a duplicidade de verdade o banco recusa na J-05.
 */
export function placaJaCadastrada(_placaNormalizada: string): boolean {
  return false;
}

/** Baixa a listagem filtrada como CSV, pela rota de exportacao, que registra o acesso. */
export function useBaixarVeiculosCsv(): (filtro: FiltroDeVeiculos) => Promise<number> {
  const consultas = useQueryClient();

  return async ({ pagina: _pagina, tamanho: _tamanho, ...recorte }: FiltroDeVeiculos) => {
    const resposta = await consultas.fetchQuery<PaginaComPermissoes<Veiculo>>({
      queryKey: CHAVES_DE_VEICULOS.exportacao(recorte),
      queryFn: () =>
        chamarApi<PaginaComPermissoes<Veiculo>>(
          `${CAMINHO_DA_API}/exportacao${parametrosDaListagem({ ...recorte })}`,
        ),
      staleTime: 0,
    });

    exportarCsv(
      'veiculos.csv',
      [
        'CRM',
        'Placa',
        'Marca',
        'Modelo',
        'Capacidade TEG',
        'Credenciado',
        'Seguradora',
        'Cadastro',
        'Situação',
      ],
      resposta.itens.map((veiculo) => [
        veiculo.crm ?? '',
        mascaraPlaca(veiculo.placa),
        veiculo.marca ?? '',
        veiculo.modelo ?? '',
        veiculo.capacidade_teg != null ? String(veiculo.capacidade_teg) : '',
        veiculo.credenciados?.nome ?? '',
        veiculo.seguro_seguradora ?? '',
        veiculo.estado_cadastro ?? '',
        veiculo.status,
      ]),
    );

    return resposta.itens.length;
  };
}
